import {useEffect, useRef} from 'react'
import {copilotDockedPanelClosedEvent, copilotDockedPanelReadyEvent} from '@github-ui/copilot-chat-helpers/constants'
import {subscribeAddCopilotChatReference, subscribeOpenCopilotChat} from '@github-ui/copilot-chat-events'
import {useCopilotSidePanelActions, useIsCopilotSidePanelOpen} from './copilot-side-panel-context'

/**
 * Hook that integrates the copilot side panel with the global copilot chat system:
 * 1. Suppresses the embedded immersive floating panel on mount, resumes on unmount
 * 2. Intercepts open-copilot-chat events → opens side panel
 * 3. Intercepts add-copilot-chat-reference events → opens side panel
 *
 * Returns a ref to attach to the toggle button element.
 */
export function useCopilotSidePanelIntegration() {
  const isOpen = useIsCopilotSidePanelOpen()
  const actions = useCopilotSidePanelActions()
  const togglePanelRef = useRef<HTMLButtonElement>(null)

  // Notify the embedded immersive panel that a docked-panel page is
  // active on mount (suppresses floating chat) and that it's gone on
  // unmount (allows floating chat to resume).
  useEffect(() => {
    document.dispatchEvent(new CustomEvent(copilotDockedPanelReadyEvent))
    return () => {
      document.dispatchEvent(new CustomEvent(copilotDockedPanelClosedEvent))
    }
  }, [])

  // Intercept open-copilot-chat events so chat opens in the side panel
  useEffect(() => {
    return subscribeOpenCopilotChat(() => {
      if (!isOpen) {
        actions.openPanel(togglePanelRef.current)
      }
    })
  }, [actions, isOpen])

  // Intercept add-copilot-chat-reference events that request panel opening
  useEffect(() => {
    return subscribeAddCopilotChatReference(e => {
      if (e.openPanel && !isOpen) {
        actions.openPanel(togglePanelRef.current)
      }
    })
  }, [actions, isOpen])

  return {togglePanelRef, isOpen, actions}
}
